import { LoadingButton } from '@mui/lab';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import { useConfig, useNotification } from '@revenuewell/front-end-bundle';
import { ChangeEvent, FC, useEffect, useMemo, useRef, useState } from 'react';
import DirectCommunicationService from '../../../../services/practice-portal/direct-communication/direct-communication-service';
import { DirectCommunicationType } from '../../../../services/practice-portal/direct-communication/types';
import { CloseDialogButton, DialogContentWrapper, DialogSubTitle } from '../../dialog/dialog.styles';
import { TemplateEditor, TemplateEditorRefProps } from './template-editor';
import { ErrorMessage, TemplateSubjectWrapper } from './template-editor.styles';

export interface DirectMailTemplateEditorDialogProps {
  open: boolean;
  onClose?: () => void;
  onSend?: () => void;
  patientId: number;
  locationId: number;
  reviewId?: number; // required for DirectCommunicationType.PostReviewToGoogle
  directCommunicationType: DirectCommunicationType;
  title?: string;
  subject?: string;
  fromName?: string;
  attachmentFileName?: string;
  buttons?: {
    submit?: {
      title?: string;
    };
  };
}
export const DirectMailTemplateEditorDialog: FC<DirectMailTemplateEditorDialogProps> = (props) => {
  const {
    open,
    onClose,
    onSend,
    patientId,
    locationId,
    reviewId,
    directCommunicationType,
    title,
    subject,
    fromName,
    attachmentFileName,
    buttons
  } = props;

  const { config } = useConfig();
  const { showError, showSuccess } = useNotification();
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);
  const [templateUrl, setTemplateUrl] = useState<string>();
  const [templateReady, setTemplateReady] = useState<boolean>(false);
  const [templateSubject, setTemplateSubject] = useState<string>(subject || '');
  const templateSubjectRef = useRef(templateSubject);
  const templateEditorRef = useRef<TemplateEditorRefProps>(null);
  const directCommunicationService = useMemo(() => new DirectCommunicationService(config), [config]);

  useEffect(() => {
    templateSubjectRef.current = templateSubject;
  }, [templateSubject]);

  useEffect(() => {
    if (open) {
      if (!patientId || !locationId) return;

      loadTemplate(locationId, patientId, directCommunicationType, reviewId);
    } else {
      setTemplateUrl('');
      setTemplateSubject(subject || '');
      setTemplateReady(false);
    }
  }, [open, patientId, locationId, directCommunicationType]);

  const loadTemplate = async (
    locationId: number,
    patientId: number,
    directCommType: DirectCommunicationType,
    reviewId?: number
  ) => {
    try {
      const templateId = await directCommunicationService.getDirectCommunicationTemplateId(locationId, directCommType);

      const url =
        `${config.practice.url}/` +
        `DirectCommunication/GetTemplateEditor?idTemplate=${templateId}&idRecipient=${patientId}&idReview=${reviewId}`;

      setTemplateUrl(url);
    } catch {
      showError('Unable to get template editor url.');
    }
  };

  const handleEditorReady = () => setTemplateReady(true);

  const handleEditorError = (status: any, message: any) => {
    console.error('Template Editor error: ', message, status);
    showError('Template Editor error has occured.');
  };

  const handleTemplateDataReceived = async (templateData: any) => {
    try {
      const payload = {
        Subject: templateSubjectRef.current,
        FromName: `From ${fromName}`,
        InternalTemplateData: templateData
      };

      const result = await directCommunicationService.createDirectCommunication(patientId, payload, attachmentFileName);

      if (!result.IsSuccess) throw new Error();

      onSend && onSend();
    } catch {
      showError('The email was not sent.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveClick = () => {
    setLoading(true);

    if (templateEditorRef.current) templateEditorRef.current.getTemplateData();
  };

  const handleClose = () => onClose && onClose();

  const handleSujectChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setError(e.target.value ? undefined : 'Subject is required.');
    setTemplateSubject(e.target.value);
  };

  return (
    <>
      <Dialog open={open} maxWidth={false}>
        <DialogContentWrapper>
          <DialogTitle sx={{ cursor: 'default' }}>
            {title ?? 'Edit Template'}
            <CloseDialogButton onClick={handleClose} />
          </DialogTitle>
          <DialogContent sx={{ width: '880px' }}>
            <TemplateSubjectWrapper>
              <Box sx={{ marginRight: '10px' }}>Subject: </Box>
              <TextField size="small" value={templateSubject} onChange={handleSujectChange} fullWidth />
              {error && <ErrorMessage>{error}</ErrorMessage>}
            </TemplateSubjectWrapper>

            <DialogSubTitle sx={{ fontSize: '14px', color: '#828282' }}>
              Keep the subject relevant to your practice and non-spammy to avoid spam filters
            </DialogSubTitle>

            <TemplateEditor
              ref={templateEditorRef}
              url={templateUrl}
              onReady={handleEditorReady}
              onError={handleEditorError}
              onTemplateDataReceived={handleTemplateDataReceived}
            />

            <DialogActions sx={{ padding: 0, marginTop: '35px' }}>
              <Button
                onClick={handleClose}
                color="primary"
                variant="outlined"
                disableElevation
                sx={{ marginRight: 'auto' }}
              >
                Cancel
              </Button>
              <LoadingButton
                type="submit"
                loading={loading}
                disabled={!templateReady || error !== undefined}
                onClick={handleSaveClick}
                color="primary"
                variant="contained"
                disableElevation
              >
                {buttons?.submit?.title ?? 'Save'}
              </LoadingButton>
            </DialogActions>
          </DialogContent>
        </DialogContentWrapper>
      </Dialog>
    </>
  );
};
